import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { MapPin, Users, Star, Phone, Mail, Calendar as CalendarIcon, Check, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("overview");
  const [month, setMonth] = useState(() => new Date());
  const [inquiry, setInquiry] = useState({ name: "", phone: "", email: "", event_date: "", pax: "", message: "" });

  useEffect(() => {
    api.get(`/venues/${id}`).then((r)=>setV(r.data));
    api.get(`/venues/${id}/bookings`).then((r)=>setBookings(r.data));
  }, [id]);

  const bookedSet = useMemo(() => new Set(bookings.map(b => b.date)), [bookings]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inquiries", { venue_id: id, ...inquiry, pax: inquiry.pax ? Number(inquiry.pax) : undefined });
      toast.success("Inquiry sent! The venue will contact you shortly.");
      setInquiry({ name: "", phone: "", email: "", event_date: "", pax: "", message: "" });
    } catch (e) {
      toast.error("Failed to send inquiry");
    }
  };

  if (!v) return <div className="max-w-7xl mx-auto px-6 py-16"><div className="h-96 skeleton"/></div>;

  const catBg = {
    banquet_hall: "https://images.pexels.com/photos/33852468/pexels-photo-33852468.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
    lawn: "https://images.pexels.com/photos/37331160/pexels-photo-37331160.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
    catering: "https://images.pexels.com/photos/36766881/pexels-photo-36766881.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
  }[v.category];

  return (
    <div>
      {/* Hero gallery */}
      <section className="relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5">
          <div className="md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto md:h-[520px] overflow-hidden">
            <img src={v.images?.[0]} className="w-full h-full object-cover" alt={v.name}/>
          </div>
          {(v.images || []).slice(1, 5).map((im, i)=>(
            <div key={i} className="hidden md:block h-[257px] overflow-hidden">
              <img src={im} className="w-full h-full object-cover" alt={`${v.name} ${i+2}`}/>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            {v.highlighted && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest-2 px-2 py-1 rounded-full bg-[color:var(--gold)] text-black mb-3">
                <Sparkles className="w-3 h-3"/> Premium Venue
              </span>
            )}
            <h1 className="font-display text-4xl md:text-5xl">{v.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4"/> {v.address}, {v.city}</span>
              <span className="inline-flex items-center gap-1"><Users className="w-4 h-4"/> {v.pax_min}–{v.pax_max} guests</span>
              {v.rating > 0 && <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[color:var(--gold)] text-[color:var(--gold)]"/> {v.rating} ({v.reviews_count})</span>}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-[color:var(--line)] flex gap-6 overflow-x-auto">
            {["overview","packages","calendar","catering","gallery","reviews","faqs"].map(t=>(
              <button key={t} onClick={()=>setTab(t)} className={`text-sm py-3 uppercase tracking-widest-2 ${tab===t ? "text-[color:var(--wine)] border-b-2 border-[color:var(--wine)]" : "text-neutral-500"}`} data-testid={`tab-${t}`}>{t}</button>
            ))}
          </div>

          {tab==="overview" && (
            <div className="space-y-6">
              <p className="text-neutral-700 leading-relaxed">{v.description}</p>
              <div>
                <h3 className="font-display text-2xl mb-3">Amenities</h3>
                <div className="grid grid-cols-2 gap-2">
                  {(v.amenities||[]).map((a,i)=>(
                    <div key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[color:var(--emerald)]"/> {a}</div>
                  ))}
                </div>
              </div>
              {v.videos?.length > 0 && (
                <div>
                  <h3 className="font-display text-2xl mb-3">Videos</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {v.videos.map((yt,i)=>(
                      <div key={i} className="aspect-video rounded-2xl overflow-hidden"><iframe src={yt} className="w-full h-full" allowFullScreen title={`video-${i}`}/></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab==="packages" && (
            <div className="grid md:grid-cols-2 gap-4">
              {(v.packages||[]).map((p,i)=>(
                <div key={i} className="card-luxury p-6">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-2xl">{p.name}</div>
                    <div className="text-[color:var(--wine)] font-semibold">₹{Number(p.price).toLocaleString("en-IN")}</div>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {(p.includes||[]).map((it,j)=><li key={j} className="flex items-center gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)]"/> {it}</li>)}
                  </ul>
                </div>
              ))}
              {!(v.packages||[]).length && <div className="text-neutral-500 text-sm">Owner hasn't uploaded packages yet.</div>}
            </div>
          )}

          {tab==="calendar" && (
            <TwoYearCalendar month={month} setMonth={setMonth} bookedSet={bookedSet} parallel={v.parallel_slots||1} bookings={bookings}/>
          )}

          {tab==="catering" && (
            <div>
              <h3 className="font-display text-2xl mb-3">Cuisine Options</h3>
              <div className="flex gap-3 mb-4">
                {v.veg_options && <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs">Pure Veg</span>}
                {v.non_veg_options && <span className="px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs">Non-Veg</span>}
              </div>
              <ul className="space-y-2 text-sm">
                {(v.catering_menu||[]).map((m,i)=><li key={i} className="flex justify-between border-b py-2"><span>{m.name}</span><span>₹{m.price}/plate</span></li>)}
                {!(v.catering_menu||[]).length && <li className="text-neutral-500">Catering menu coming soon.</li>}
              </ul>
            </div>
          )}

          {tab==="gallery" && (
            <div className="grid md:grid-cols-3 gap-3">
              {(v.images||[]).map((im,i)=>(<img key={i} src={im} className="rounded-xl aspect-square object-cover" alt={`gallery-${i}`}/>))}
            </div>
          )}

          {tab==="reviews" && (
            <div className="space-y-4">
              <div className="text-sm text-neutral-600">Overall rating: <span className="font-semibold text-[color:var(--wine)]">{v.rating || 0}</span> from {v.reviews_count||0} reviews</div>
              <div className="text-neutral-500 text-sm">Reviews module coming soon. Meanwhile, contact the venue for real Google reviews.</div>
            </div>
          )}

          {tab==="faqs" && (
            <div className="space-y-3">
              {(v.faqs||[]).map((f,i)=>(
                <details key={i} className="card-luxury p-4">
                  <summary className="cursor-pointer font-medium">{f.q}</summary>
                  <p className="mt-2 text-sm text-neutral-700">{f.a}</p>
                </details>
              ))}
              {!(v.faqs||[]).length && <div className="text-neutral-500 text-sm">No FAQs yet.</div>}
            </div>
          )}
        </div>

        {/* Sticky inquiry card */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="card-luxury p-6">
            <div className="flex items-baseline gap-2">
              <span className="text-neutral-500 text-xs">Starting from</span>
              <span className="font-display text-3xl text-[color:var(--wine)]">₹{Number(v.price_per_day||0).toLocaleString("en-IN")}</span>
              <span className="text-xs text-neutral-500">/day</span>
            </div>
            <form onSubmit={submitInquiry} className="mt-4 space-y-2" data-testid="inquiry-form">
              <input value={inquiry.name} onChange={e=>setInquiry({...inquiry, name:e.target.value})} required placeholder="Your name" className="w-full px-3 py-2.5 rounded-lg border" data-testid="inq-name"/>
              <input value={inquiry.phone} onChange={e=>setInquiry({...inquiry, phone:e.target.value})} required placeholder="Phone" className="w-full px-3 py-2.5 rounded-lg border" data-testid="inq-phone"/>
              <input value={inquiry.email} onChange={e=>setInquiry({...inquiry, email:e.target.value})} placeholder="Email (optional)" className="w-full px-3 py-2.5 rounded-lg border"/>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={inquiry.event_date} onChange={e=>setInquiry({...inquiry, event_date:e.target.value})} className="px-3 py-2.5 rounded-lg border"/>
                <input type="number" value={inquiry.pax} onChange={e=>setInquiry({...inquiry, pax:e.target.value})} placeholder="Guests" className="px-3 py-2.5 rounded-lg border"/>
              </div>
              <textarea value={inquiry.message} onChange={e=>setInquiry({...inquiry, message:e.target.value})} placeholder="Message" rows={3} className="w-full px-3 py-2.5 rounded-lg border"/>
              <button className="btn-wine w-full justify-center" data-testid="inq-submit">Send inquiry</button>
            </form>
            <div className="mt-4 border-t pt-4 space-y-2 text-sm">
              {v.contact_phone && <a href={`tel:${v.contact_phone}`} className="flex items-center gap-2 text-neutral-700"><Phone className="w-4 h-4"/> {v.contact_phone}</a>}
              {v.contact_email && <a href={`mailto:${v.contact_email}`} className="flex items-center gap-2 text-neutral-700"><Mail className="w-4 h-4"/> {v.contact_email}</a>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function TwoYearCalendar({ month, setMonth, bookedSet, parallel, bookings }) {
  // Show 3 months at a time
  const months = [0,1,2].map((off)=>{
    const m = new Date(month.getFullYear(), month.getMonth()+off, 1);
    return m;
  });
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button className="btn-ghost !py-1.5 !px-3 text-xs" onClick={()=>setMonth(new Date(month.getFullYear(), month.getMonth()-1, 1))}>← Prev</button>
        <div className="text-sm text-neutral-600">Availability for next 24 months. Booked dates are marked in red.</div>
        <button className="btn-ghost !py-1.5 !px-3 text-xs ml-auto" onClick={()=>setMonth(new Date(month.getFullYear(), month.getMonth()+1, 1))}>Next →</button>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {months.map((m)=><MonthGrid key={m.toISOString()} m={m} bookedSet={bookedSet} parallel={parallel} bookings={bookings}/>)}
      </div>
      <div className="flex gap-4 text-xs mt-6">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-500/80"/> Fully booked</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-300"/> Partial (catering slots)</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-emerald-500/70"/> Available</span>
      </div>
    </div>
  );
}

function MonthGrid({ m, bookedSet, parallel, bookings }) {
  const first = new Date(m.getFullYear(), m.getMonth(), 1);
  const days = new Date(m.getFullYear(), m.getMonth()+1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Mon start
  const cells = [];
  for (let i=0;i<startDow;i++) cells.push(null);
  for (let d=1; d<=days; d++) cells.push(d);
  const fmt = (d)=>`${m.getFullYear()}-${String(m.getMonth()+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const countFor = (d)=> bookings.filter(b => b.date === fmt(d)).length;

  return (
    <div className="card-luxury p-4">
      <div className="text-center font-display text-lg mb-2">{m.toLocaleString("en", { month: "long", year: "numeric" })}</div>
      <div className="grid grid-cols-7 text-[10px] text-neutral-500 uppercase tracking-widest-2 text-center mb-1">
        {["M","T","W","T","F","S","S"].map((d,i)=><div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i)=>{
          if (!c) return <div key={i}/>;
          const bcount = countFor(c);
          const fully = bcount >= (parallel||1);
          const partial = bcount > 0 && !fully;
          const cls = fully ? "bg-red-500/80 text-white" : partial ? "bg-amber-300 text-black" : "bg-emerald-50 text-neutral-700";
          return <div key={i} className={`aspect-square rounded-md text-xs flex items-center justify-center ${cls}`}>{c}</div>;
        })}
      </div>
    </div>
  );
}
